import { useEffect, useState } from "react";
import { supabase } from "../../supabase";
import { useHistory } from "react-router-dom";

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const history = useHistory();

  useEffect(() => {
    async function load() {
      const now = new Date();
      const kst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
      const today = kst.toISOString().slice(0, 10) + "T00:00:00+09:00";

      const [
        { count: userCount },
        { count: galleryCount },
        { count: pendingCount },
        { count: todayUserCount },
        { count: todayDownloadCount },
        { count: fileCount },
      ] = await Promise.all([
        supabase.from("profiles").select("*", { count: "exact", head: true }).neq("role", "admin"),
        supabase.from("gallery").select("*", { count: "exact", head: true }),
        supabase.from("gallery").select("*", { count: "exact", head: true }).eq("status", "pending"),
        supabase.from("profiles").select("*", { count: "exact", head: true }).neq("role", "admin").gte("created_at", today),
        supabase.from("download_logs").select("*", { count: "exact", head: true }).gte("created_at", today),
        supabase.from("user_files").select("*", { count: "exact", head: true }).eq("is_deleted", false),
      ]);

      // 관리자 ID 목록
      const { data: adminProfiles } = await supabase
        .from("profiles")
        .select("id")
        .eq("role", "admin");
      const adminIdSet = new Set((adminProfiles || []).map((p) => p.id));

      // 활성 구독 (FREE, 관리자 제외)
      const { data: activeSubs, error: subError } = await supabase
        .from("subscriptions")
        .select("user_id, plan")
        .eq("status", "active");
      const activeSubUsers = new Set(
        (activeSubs || [])
          .filter((s) => s.plan !== "FREE" && !adminIdSet.has(s.user_id))
          .map((s) => s.user_id)
      );
      const activeSubCount = activeSubUsers.size;

      // 오늘 방문자 (고유 user_id 기준, 관리자 제외)
      const { data: todayPageViews } = await supabase
        .from("page_views")
        .select("user_id")
        .gte("created_at", today);
      const uniqueVisitors = new Set(
        (todayPageViews || [])
          .filter((pv) => pv.user_id && !adminIdSet.has(pv.user_id))
          .map((pv) => pv.user_id)
      );
      const todayVisitorCount = uniqueVisitors.size;

      const { data: recentUsers } = await supabase
        .from("profiles")
        .select("id, email, created_at")
        .order("created_at", { ascending: false })
        .limit(5);

      const { data: recentGallery } = await supabase
        .from("gallery")
        .select("id, title, created_at")
        .order("created_at", { ascending: false })
        .limit(5);

      setData({
        userCount,
        galleryCount,
        pendingCount,
        todayUserCount,
        todayDownloadCount,
        todayVisitorCount,
        activeSubCount,
        fileCount,
        recentUsers: recentUsers || [],
        recentGallery: recentGallery || [],
      });
    }

    load();
  }, []);

  if (!data) return <div>로딩 중…</div>;

  const cards = [
    { label: "전체 유저", value: data.userCount ?? 0, onClick: () => history.push("/admin/users") },
    { label: "오늘 가입", value: data.todayUserCount ?? 0, onClick: () => history.push("/admin/users") },
    { label: "전체 갤러리", value: data.galleryCount ?? 0, onClick: () => history.push("/admin/gallery") },
    {
      label: "승인 대기",
      value: data.pendingCount ?? 0,
      highlight: (data.pendingCount ?? 0) > 0,
      onClick: () => history.push("/admin/gallery"),
    },
    { label: "총 파일 수", value: data.fileCount ?? 0 },
    // "활성 구독" 칸 숨김 (2026-10-19 김현비) — 요금제를 닫아 볼 일이 없다
    { label: "오늘 다운로드", value: data.todayDownloadCount ?? 0 },
    { label: "오늘 방문자", value: data.todayVisitorCount ?? 0 },
  ];

  return (
    <div className="admin-page">
      <div className="admin-title">대시보드</div>

      {/* 상단 요약 */}
      <div className="admin-cards">
        {cards.map((c) => (
          <div
            key={c.label}
            className={`admin-card ${c.highlight ? "highlight" : ""} ${c.onClick ? "clickable" : ""}`}
            onClick={c.onClick}
          >
            <h3>{c.label}</h3>
            <div className="big">{c.value}</div>
          </div>
        ))}
      </div>

      {/* 최근 가입 */}
      <div className="admin-section">
        <h3>최근 가입 유저</h3>
        <ul className="admin-list">
          {(data.recentUsers || []).map((u) => (
            <li key={u.id}>
              {u.email}
              <span className="admin-date">
                {new Date(u.created_at).toLocaleDateString()}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* 최근 갤러리 */}
      <div className="admin-section">
        <h3>최근 갤러리</h3>
        <ul className="admin-list">
          {(data.recentGallery || []).map((g) => (
            <li key={g.id}>
              {g.title}
              <span className="admin-date">
                {new Date(g.created_at).toLocaleDateString()}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
